import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Animated, ScrollView, TouchableOpacity, View } from 'react-native'
import { useFocusEffect, useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@style/service'
import { AccountAvatarNavigationBar } from '@components/navigationBar/AccountAvatarNavigationBar'
import { Icon, Text } from '@components/base'
import { CommonRefreshControl } from '@components'
import { useHasLargeScreen } from '@config/device'
import 'react-native-get-random-values'
import { useLazyScreenPreload } from '@navigation/utils'
import { useDispatchAsync, useSnack } from '@utils'
import {
  CONVERSATIONS_LOADING_ERROR,
  CONVERSATIONS_SORTS,
  HOME_CONVERSATIONS_PREVIEW_LIMIT,
  HistoryView,
  PINNED_CONVERSATIONS_FILTER,
  PROMPTS_LOADING_ERROR,
} from '@screens/Nora/constants'
import {
  chatbotQuickPromptsSelector,
  chatbotReportsEligibleSelector,
  pinnedConversationsCountSelector,
  pinnedConversationsSelector,
  recentConversationsSelector,
} from '@screens/Nora/models/nora.selectors'
import { userSelector } from '@selectors/app'
import { uiFlagsSelector } from '@selectors/uiFlags'
import { AppStackParamList } from '@src/navigation/types'
import { UiStateNames } from '@src/types'
import { useIsDarkMode } from '@config/theme.ts'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { AskNoraInput } from './components/AskNoraInput/AskNoraInput'
import { ConversationCard } from './components/ConversationCard'
import { LearnHubCarousel } from './components/LearnHubCarousel'
import {
  NoraHomeWalkthrough,
  NoraHomeWalkthroughProvider,
  NoraHomeWalkthroughTarget,
  NoraHomeWalkthroughTargetId,
} from './components/NoraHomeWalkthrough'
import { ReportsNotEligibleCard } from './components/ReportsNotEligibleCard'
import { useNoraStreamChat } from './context/NoraStreamChatContext'
import { NoraFileAttachmentsProvider } from './context/NoraFileAttachmentsContext'

type NoraHomeNavigationProp = StackNavigationProp<AppStackParamList>

const NORA_HOME_TUTORIAL_FINAL_PROMPT = 'What actions can you do for me?'

export const NoraHomeScreen = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyle)
  const theme = useTheme()
  const isDarkMode = useIsDarkMode()
  const hasLargeScreen = useHasLargeScreen()
  const navigation = useNavigation<NoraHomeNavigationProp>()
  const showSnack = useSnack()
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()

  const user = useSelector(userSelector)
  const uiFlags = useSelector(uiFlagsSelector)
  const recentConversations = useSelector(recentConversationsSelector)
  const pinnedConversations = useSelector(pinnedConversationsSelector)
  const pinnedConversationsCount = useSelector(pinnedConversationsCountSelector)
  const chatbotQuickPrompts = useSelector(chatbotQuickPromptsSelector)
  const chatbotReportsEligible = useSelector(chatbotReportsEligibleSelector)

  const greetingLineHeight = useRef(0)

  const [showNotEligibleCard, setShowNotEligibleCard] = useState(false)
  const [greetingWrapped, setGreetingWrapped] = useState(false)
  const [conversationsTab, setConversationsTab] = useState<'recent' | 'pinned'>('recent')
  const [refreshing, setRefreshing] = useState(false)

  const conversations =
    conversationsTab === 'pinned'
      ? pinnedConversations.slice(0, HOME_CONVERSATIONS_PREVIEW_LIMIT)
      : recentConversations.slice(0, HOME_CONVERSATIONS_PREVIEW_LIMIT)
  const isPinnedTab = conversationsTab === 'pinned'

  const {
    onAsk,
    openConversation,
    shouldReloadConversations,
    setShouldReloadConversations,
  } = useNoraStreamChat()

  const scrollViewRef = useRef<ScrollView>(null)
  const isScrollingToInput = useRef(false)

  const scrollToInput = useCallback(() => {
    if (isScrollingToInput.current) {
      return
    }
    isScrollingToInput.current = true
    scrollViewRef.current?.scrollTo({ y: 0, animated: true })
    setTimeout(() => {
      isScrollingToInput.current = false
    }, 500)
  }, [])
  const waveAnim = useRef(new Animated.Value(0)).current

  useFocusEffect(
    useCallback(() => {
      waveAnim.setValue(0)
      Animated.sequence([
        Animated.delay(300),
        ...Array(3)
          .fill(null)
          .flatMap(() => [
            Animated.timing(waveAnim, {
              toValue: 1,
              duration: 150,
              useNativeDriver: true,
            }),
            Animated.timing(waveAnim, {
              toValue: -1,
              duration: 150,
              useNativeDriver: true,
            }),
          ]),
        Animated.timing(waveAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start()
    }, [waveAnim]),
  )

  const waveRotation = waveAnim.interpolate({
    inputRange: [-1, 0, 1],
    outputRange: ['-20deg', '0deg', '20deg'],
  })

  useLazyScreenPreload('NoraConversationScreen')
  useLazyScreenPreload('NoraConversationsScreen')
  useLazyScreenPreload('CustomReportModal')
  useLazyScreenPreload('NoraReportScreen')
  useLazyScreenPreload('NoraReportsScreen')
  useLazyScreenPreload('NoraQuickPromptsScreen')
  useLazyScreenPreload('NoraSettingsScreen')

  const renderAskNoraInput = () => (
    <NoraHomeWalkthroughTarget
      targetId={NoraHomeWalkthroughTargetId.AskNoraInput}
      accessibilityLabel="Ask Nora input"
    >
      <NoraFileAttachmentsProvider>
        <AskNoraInput onType={scrollToInput} />
      </NoraFileAttachmentsProvider>
    </NoraHomeWalkthroughTarget>
  )

  const loadConversations = useCallback(() => {
    dispatch({
      type: 'nora/allChatbotConversations',
      payload: {
        sorts: CONVERSATIONS_SORTS,
        perPage: HOME_CONVERSATIONS_PREVIEW_LIMIT,
      },
      failure: () => {
        showSnack(CONVERSATIONS_LOADING_ERROR, null, 'error')
      },
    })
    dispatch({
      type: 'nora/loadPinnedConversations',
      payload: {
        sorts: CONVERSATIONS_SORTS,
        dynamicFilters: PINNED_CONVERSATIONS_FILTER,
        perPage: HOME_CONVERSATIONS_PREVIEW_LIMIT,
      },
      failure: () => {
        showSnack(CONVERSATIONS_LOADING_ERROR, null, 'error')
      },
    })
  }, [dispatch, showSnack])

  const loadQuickPrompts = useCallback(
    () =>
      dispatch({
        type: 'nora/allChatbotQuickPrompts',
        failure: () => {
          showSnack(PROMPTS_LOADING_ERROR, null, 'error')
        },
      }),
    [dispatch, showSnack],
  )

  const onRefresh = useCallback(async () => {
    setRefreshing(true)

    try {
      await Promise.all([
        dispatchAsync({
          type: 'nora/allChatbotConversations',
          payload: {
            sorts: CONVERSATIONS_SORTS,
            perPage: HOME_CONVERSATIONS_PREVIEW_LIMIT,
          },
        }),
        dispatchAsync({
          type: 'nora/loadPinnedConversations',
          payload: {
            sorts: CONVERSATIONS_SORTS,
            dynamicFilters: PINNED_CONVERSATIONS_FILTER,
            perPage: HOME_CONVERSATIONS_PREVIEW_LIMIT,
          },
        }),
        dispatchAsync({
          type: 'nora/allChatbotQuickPrompts',
        }),
        dispatchAsync({
          type: 'nora/fetchChatbotReportsEligible',
        }).catch(() => undefined),
      ])
    } catch {
      showSnack(CONVERSATIONS_LOADING_ERROR, null, 'error')
    } finally {
      setRefreshing(false)
    }
  }, [dispatchAsync, showSnack])

  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  useEffect(() => {
    loadQuickPrompts()
  }, [loadQuickPrompts])

  useFocusEffect(
    useCallback(() => {
      if (shouldReloadConversations) {
        loadConversations()
        setShouldReloadConversations(false)
      }
    }, [shouldReloadConversations, loadConversations, setShouldReloadConversations]),
  )

  useFocusEffect(
    useCallback(() => {
      dispatch({ type: 'nora/fetchChatbotReportsEligible' })
    }, [dispatch]),
  )

  const handleQuickPromptPress = async (prompt: { id: string; text: string }) => {
    await onAsk({ prompt: prompt.text, quickPromptId: prompt.id })
  }

  const openCustomReport = () => {
    if (chatbotReportsEligible === false) {
      setShowNotEligibleCard(true)
      return
    }
    navigation.navigate('CustomReportModal')
  }

  const openHistory = (view: HistoryView) => {
    navigation.navigate('NoraConversationsScreen', { filter: view })
  }

  const openQuickPrompts = () => {
    navigation.navigate('NoraQuickPromptsScreen')
  }

  const openSettings = () => {
    navigation.navigate('NoraSettingsScreen')
  }

  const closeNoraHomeTutorial = () => {
    dispatch({
      type: 'uiFlags/updateUiFlags',
      payload: { showNoraHomeTutorial: false },
    })
  }

  const completeNoraHomeTutorial = () => {
    closeNoraHomeTutorial()
    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.NoraHomeTutorialSeen,
        value: true,
      },
    })
    onAsk({ prompt: NORA_HOME_TUTORIAL_FINAL_PROMPT })
  }

  const settingsIcon = (
    <View style={styles.settingsAccessorySpacing}>
      <NoraHomeWalkthroughTarget
        targetId={NoraHomeWalkthroughTargetId.Settings}
        accessibilityLabel="Settings"
        style={styles.settingsWalkthroughTarget}
      >
        <TouchableOpacity
          style={styles.settingsButton}
          onPress={openSettings}
          accessibilityLabel="Settings"
        >
          <Icon name="gear" />
        </TouchableOpacity>
      </NoraHomeWalkthroughTarget>
    </View>
  )

  const rightAccessories = [settingsIcon]

  return (
    <NoraHomeWalkthroughProvider
      onClose={closeNoraHomeTutorial}
      onComplete={completeNoraHomeTutorial}
    >
      <AccountAvatarNavigationBar
        style={styles.container}
        title={<View />}
        hideLeftIcon={hasLargeScreen}
        rightAccessories={rightAccessories}
      >
        <ScrollView
          ref={scrollViewRef}
          contentContainerStyle={[styles.content, styles.contentBackground]}
          refreshControl={<CommonRefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          <View style={styles.greetingSection}>
            <View style={styles.greetingContent}>
              <View
                style={styles.greetingRow}
                onLayout={(e) => {
                  const height = e.nativeEvent.layout.height
                  if (greetingLineHeight.current === 0) {
                    greetingLineHeight.current = height
                  } else if (height > greetingLineHeight.current) {
                    setGreetingWrapped(true)
                  }
                }}
              >
                <Text type={greetingWrapped ? 'large' : 'title-2'} style={styles.textSecondary}>
                  Hi {user?.firstName}{' '}
                </Text>
                <Animated.View style={{ transform: [{ rotate: waveRotation }] }}>
                  <Text type="title-2">👋</Text>
                </Animated.View>
                <Text type="title-2">{greetingWrapped ? '' : ', '}Nora here</Text>
              </View>
            </View>
            {renderAskNoraInput()}
          </View>
          <View>
            <View style={styles.sectionHeader}>
              <Text type="regular" bold style={styles.uppercase}>
                Reports
              </Text>
              <Text
                type="regular"
                bold
                style={[styles.link, styles.uppercase]}
                onPress={() => openHistory(HistoryView.REPORTS)}
              >
                See All
              </Text>
            </View>
            <TouchableOpacity
              style={[styles.card, styles.increasedVerticalPadding, isDarkMode && styles.cardDark]}
              accessibilityLabel="My Health Report"
              onPress={openCustomReport}
            >
              <View style={[styles.icon, styles.reportIcon, isDarkMode && styles.reportIconDark]}>
                <Icon name="chart-line" size="22" color={theme['theme.primary.base']} />
              </View>
              <View style={styles.flex1}>
                <Text type="large" bold>
                  My Health Report
                </Text>
                <Text type="regular" style={styles.textSecondary}>
                  See how your glucose, meals, activity, and sleep connect
                </Text>
              </View>
              <Icon
                name="caret-right"
                size="20"
                weight="bold"
                color={theme['theme.text.tertiary']}
              />
            </TouchableOpacity>
            {showNotEligibleCard && !chatbotReportsEligible && (
              <View style={styles.eligibilityCardWrapper}>
                <ReportsNotEligibleCard onDismiss={() => setShowNotEligibleCard(false)} />
              </View>
            )}
          </View>
          <View>
            <NoraHomeWalkthroughTarget
              targetId={NoraHomeWalkthroughTargetId.QuickPromptsHeader}
              accessibilityLabel="Quick Prompts"
            >
              <View style={styles.sectionHeader}>
                <View>
                  <Text type="regular" bold style={styles.uppercase}>
                    Quick Prompts
                  </Text>
                  <Text type="regular" style={styles.textSecondary}>
                    Tap to start a conversation
                  </Text>
                </View>
                <Text
                  type="regular"
                  bold
                  style={[styles.link, styles.uppercase]}
                  onPress={openQuickPrompts}
                >
                  Manage
                </Text>
              </View>
            </NoraHomeWalkthroughTarget>
            {chatbotQuickPrompts.length > 0 && (
              <View style={styles.gap12}>
                {chatbotQuickPrompts.slice(0, 4).map((prompt) => (
                  <TouchableOpacity
                    key={prompt.id}
                    style={[styles.card, isDarkMode && styles.cardDark]}
                    accessibilityLabel={prompt.title}
                    onPress={() => handleQuickPromptPress(prompt)}
                  >
                    <View style={[styles.icon, styles.iconSmall]}>
                      <Icon name="lightning" size="16" />
                    </View>
                    <View style={styles.flex1}>
                      <Text type="large" bold numberOfLines={1}>
                        {prompt.title}
                      </Text>
                      <Text type="regular" numberOfLines={2} style={styles.textSecondary}>
                        {prompt.text}
                      </Text>
                    </View>
                    <Icon
                      name="caret-right"
                      size="20"
                      weight="bold"
                      color={theme['theme.text.tertiary']}
                    />
                  </TouchableOpacity>
                ))}
              </View>
            )}
            {!chatbotQuickPrompts.length && (
              <View style={[styles.emptyState, isDarkMode && styles.emptyStateDark]}>
                <Icon name="lightning" size="24" style={styles.textSecondary} />
                <Text type="small" style={styles.textSecondary}>
                  Add quick prompts to speed up your conversations
                </Text>
              </View>
            )}
          </View>
          <View>
            <NoraHomeWalkthroughTarget
              targetId={NoraHomeWalkthroughTargetId.ConversationsHeader}
              accessibilityLabel="Conversations"
            >
              <View style={styles.sectionHeader}>
                <Text type="regular" bold style={styles.uppercase}>
                  Conversations
                </Text>
                <Text
                  type="regular"
                  bold
                  style={[styles.link, styles.uppercase]}
                  onPress={() => openHistory(isPinnedTab ? HistoryView.PINNED : HistoryView.ALL)}
                >
                  See All
                </Text>
              </View>
            </NoraHomeWalkthroughTarget>
            <View style={styles.tabContainer}>
              <TouchableOpacity
                style={[styles.tab, conversationsTab === 'recent' && styles.tabActive]}
                onPress={() => setConversationsTab('recent')}
                accessibilityLabel="Recent conversations"
              >
                <Text
                  type="regular"
                  bold
                  style={conversationsTab === 'recent' ? undefined : styles.textTertiary}
                >
                  Recent
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.tab, conversationsTab === 'pinned' && styles.tabActive]}
                onPress={() => setConversationsTab('pinned')}
                accessibilityLabel="Pinned conversations"
              >
                <View style={styles.tabContent}>
                  <Icon name="push-pin" size="14" color={theme['theme.text.tertiary']} />
                  <Text
                    type="regular"
                    bold
                    style={conversationsTab === 'pinned' ? undefined : styles.textTertiary}
                  >
                    Pinned
                  </Text>
                  {pinnedConversationsCount > 0 && (
                    <View
                      style={[
                        styles.countHighlight,
                        conversationsTab !== 'pinned' && styles.countHighlightInactive,
                      ]}
                    >
                      <Text type="small" bold style={styles.textTertiary}>
                        {pinnedConversationsCount}
                      </Text>
                    </View>
                  )}
                </View>
              </TouchableOpacity>
            </View>
            {conversations.length === 0 && !isPinnedTab && (
              <View style={[styles.emptyState, isDarkMode && styles.emptyStateDark]}>
                <Icon name="chat-circle" size="24" style={styles.textSecondary} />
                <Text type="small" style={styles.textSecondary}>
                  Start a conversation with Nora
                </Text>
              </View>
            )}
            {conversations.length === 0 && isPinnedTab && (
              <View style={[styles.emptyState, isDarkMode && styles.emptyStateDark]}>
                <View
                  style={[
                    styles.icon,
                    styles.pinnedEmptyIcon,
                    isDarkMode && styles.pinnedEmptyIconDark,
                  ]}
                >
                  <Icon name="push-pin" size="24" />
                </View>
                <View style={styles.width300}>
                  <Text type="large" bold style={styles.textCenter}>
                    No pinned conversations
                  </Text>
                  <Text type="regular" style={[styles.textSecondary, styles.textCenter]}>
                    Pin conversations to keep your most important chats front and center.
                  </Text>
                </View>
                <View
                  style={[styles.pinnedEmptyHintCard, isDarkMode && styles.pinnedEmptyHintCardDark]}
                >
                  <View style={[styles.icon, styles.pinnedEmptyHintIcon]}>
                    <Icon name="lightning" size="16" />
                  </View>
                  <View style={styles.flex1}>
                    <Text type="regular" bold>
                      Swipe right on any conversation
                    </Text>
                    <Text type="small" style={styles.textSecondary}>
                      to pin it and find it here instantly
                    </Text>
                  </View>
                  <Icon
                    name="arrow-line-right"
                    size="20"
                    weight="bold"
                    color={theme['theme.surface.base0']}
                  />
                </View>
              </View>
            )}
            {conversations.length > 0 && (
              <View style={styles.gap12}>
                {conversations.map((conversation) => (
                  <ConversationCard
                    key={conversation.id}
                    conversation={conversation}
                    onPress={openConversation}
                    previewLines={1}
                  />
                ))}
              </View>
            )}
          </View>
          <View>
            <View style={styles.sectionHeader}>
              <Text type="regular" bold style={styles.uppercase}>
                Learn Hub
              </Text>
              <Text
                type="regular"
                bold
                style={[styles.link, styles.uppercase]}
                onPress={() => navigation.navigate('CoursesLearn')}
              >
                See All
              </Text>
            </View>
            <LearnHubCarousel />
          </View>
        </ScrollView>
        {uiFlags.showNoraHomeTutorial && <NoraHomeWalkthrough scrollViewRef={scrollViewRef} />}
      </AccountAvatarNavigationBar>
    </NoraHomeWalkthroughProvider>
  )
}

const themedStyle = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  content: {
    padding: 16,
    gap: 16,
  },
  contentBackground: {
    backgroundColor: 'theme.surface.base3',
  },
  textSecondary: {
    color: 'theme.text.secondary',
  },
  textTertiary: {
    color: 'theme.text.tertiary',
  },
  link: {
    color: 'theme.info.base',
  },
  settingsAccessorySpacing: {
    marginRight: 6,
  },
  settingsWalkthroughTarget: {
    alignItems: 'center',
    height: 44,
    justifyContent: 'center',
    width: 44,
  },
  settingsButton: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    width: '100%',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 8,
    marginBottom: 12,
  },
  card: {
    backgroundColor: 'theme.background',
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  cardDark: {
    backgroundColor: 'theme.surface.base1',
  },
  greetingSection: {
    backgroundColor: 'theme.background',
    margin: -16,
    marginBottom: 0,
    padding: 16,
    gap: 16,
    shadowColor: 'theme.text.primary',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 4,
  },
  greetingContent: {
    alignItems: 'center',
    gap: 8,
  },
  greetingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    paddingHorizontal: 24,
  },
  gap12: {
    gap: 12,
  },
  icon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: 'theme.background',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconSmall: {
    backgroundColor: 'theme.surface.base2',
    width: 32,
    height: 32,
  },
  reportIcon: {
    backgroundColor: 'theme.success.lightest',
    width: 44,
    height: 44,
    borderRadius: 14,
  },
  reportIconDark: {
    backgroundColor: 'theme.surface.base3',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 20,
    paddingVertical: 30,
    backgroundColor: 'theme.background',
    borderRadius: 16,
  },
  emptyStateDark: {
    backgroundColor: 'theme.surface.base1',
  },
  tabContainer: {
    flexDirection: 'row',
    backgroundColor: 'theme.surface.base1',
    borderRadius: 16,
    padding: 4,
    marginBottom: 12,
  },
  tab: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    borderRadius: 12,
  },
  tabActive: {
    backgroundColor: 'theme.background',
  },
  tabContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  uppercase: {
    textTransform: 'uppercase',
  },
  flex1: {
    flex: 1,
  },
  increasedVerticalPadding: {
    paddingVertical: 20,
  },
  eligibilityCardWrapper: {
    marginTop: 12,
  },
  textCenter: {
    textAlign: 'center',
  },
  pinnedEmptyHintCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 8,
    backgroundColor: 'theme.surface.base1',
    marginTop: 8,
    width: '100%',
    borderWidth: 1,
    borderColor: 'theme.surface.base',
  },
  pinnedEmptyHintCardDark: {
    backgroundColor: 'theme.surface.base2',
  },
  pinnedEmptyIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'theme.surface.base1',
  },
  pinnedEmptyIconDark: {
    backgroundColor: 'theme.surface.base2',
  },
  pinnedEmptyHintIcon: {
    backgroundColor: 'theme.surface.base0',
    width: 32,
    height: 32,
  },
  countHighlight: {
    backgroundColor: 'theme.surface.base2',
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countHighlightInactive: {
    backgroundColor: 'theme.surface.base1',
  },
  width300: {
    width: 300,
  },
})
